// Centralised, default-aware feature gating.
//
// Two config layers hold feature flags:
//   - subscription / reservation-class  -> `ConfigFeatures`     (state.context.currentResa.config.features)
//   - per-group packet                  -> `PacketConfigFeatures` + `PacketConfigPayment`
//
// A feature is "on" when it is explicitly `true`, or — when unset — when its SPEC DEFAULT is true.
// Resolving against the declared defaults (defaultReservationConfig / defaultPacketConfig) means a
// default-OFF feature such as `enableFlowtingSlots` or `enableCharging` is hidden automatically,
// without any per-call-site `=== true` special-casing.

import { defaultPacketConfig, type PacketConfigFeatures, type PacketConfigPayment } from '@/group/domain/packet';
import { defaultReservationConfig, type ConfigAdminOptions, type ConfigFeatures } from '@/subscription/domain/resaClass';

// The subscription feature flags that gate UI (excludes the `*Overridable` twins and admin flags).
export type FeatureKey =
    | 'enablePersonalFleet'
    | 'enableGuest'
    | 'enableReservation'
    | 'enableAccessProfile'
    | 'enableGroupFleet'
    | 'enableDistantRetrieval'
    | 'enableFlowtingSlots'
    | 'useUserGroupAdminAsFleetAdmin';

export type PaymentKey = keyof PacketConfigPayment;

const featureDefaults = defaultReservationConfig.features ?? {};
const packetFeatureDefaults = defaultPacketConfig.features ?? {};
const paymentDefaults = defaultPacketConfig.payment ?? {};

/** Whether a subscription feature is enabled (explicit value, else its spec default, else false). */
export const isFeatureEnabled = (features: ConfigFeatures | undefined, key: FeatureKey): boolean =>
    (features?.[key] ?? featureDefaults[key] ?? false) === true;

/** Whether a user-group packet is allowed to override a subscription feature. */
export const isFeatureOverridable = (features: ConfigFeatures | undefined, key: FeatureKey): boolean =>
    (features?.[`${key}Overridable` as keyof ConfigFeatures] ?? false) === true;

/**
 * Whether the group edit form should show a toggle for a feature: the subscription must enable it
 * AND mark it overridable (there is nothing to edit for a feature the group cannot change).
 */
export const canOverrideFeature = (features: ConfigFeatures | undefined, key: FeatureKey): boolean =>
    isFeatureEnabled(features, key) && isFeatureOverridable(features, key);

/**
 * The effective per-group value of a feature: the subscription must enable it; the packet value
 * applies only when the subscription marks the feature overridable AND the packet actually sets it,
 * otherwise the subscription value wins. Use this wherever a group's real feature state matters
 * (e.g. which detail tabs to show), so subscription and packet can never disagree.
 */
export const resolveEffectiveFeature = (
    subFeatures: ConfigFeatures | undefined,
    packetFeatures: PacketConfigFeatures | undefined,
    key: FeatureKey,
): boolean => {
    if (!isFeatureEnabled(subFeatures, key)) return false;
    const packetValue = (packetFeatures as Record<string, boolean | undefined> | undefined)?.[key];
    if (isFeatureOverridable(subFeatures, key) && packetValue !== undefined) {
        return packetValue === true;
    }
    return true;
};

/**
 * Whether a group's OWN fleet feature is on for that group.
 *
 * Like `resolveEffectiveFeature('enableGroupFleet')`, except an explicit packet value ALWAYS wins —
 * it does not require the subscription to store the `enableGroupFleetOverridable` twin.
 *
 * That twin is read strictly (`?? false`, no default fallback), so any subscription record that never
 * stored it would silently discard the group's answer. And the group form offers "allow vehicles"
 * whenever the subscription merely ENABLES group fleets (`isFeatureEnabled`, not
 * `canOverrideFeature`) — so without this the toggle is shown, persisted, and then ignored, leaving an
 * empty Fleets tab on a group that explicitly answered "no".
 *
 * An UNSET packet value still falls back to the subscription, so groups that never answered keep
 * their tab.
 */
export const isGroupFleetEnabled = (
    subFeatures: ConfigFeatures | undefined,
    packetFeatures: PacketConfigFeatures | undefined,
): boolean => {
    if (!isFeatureEnabled(subFeatures, 'enableGroupFleet')) return false;
    const packetValue = packetFeatures?.enableGroupFleet;
    return packetValue === undefined ? true : packetValue === true;
};

/** Whether a packet feature is enabled (explicit value, else its spec default, else false). */
export const isPacketFeatureEnabled = (
    features: PacketConfigFeatures | undefined,
    key: keyof PacketConfigFeatures,
): boolean => (features?.[key] ?? packetFeatureDefaults[key] ?? false) === true;

/**
 * Whether the end-user personal fleet ("My vehicles") is available.
 *
 * The subscription gate comes first: `features.enablePersonalFleet` off → hidden, whatever the packets
 * say. When the subscription also marks the feature overridable, the user's group packets get a say,
 * and it takes only ONE of them to keep the fleet — it is hidden only when EVERY packet of the current
 * subscription turns it off. Packet values resolve against the packet spec default (`true`), so a
 * group that never stored the flag counts as a "yes", not as a denial.
 *
 * Fail-open on an empty list: the groups are fetched asynchronously, and an empty store means "not
 * known yet", not "every group said no" — hiding there would blink the fleets UI away on every load.
 */
export const isPersonalFleetEnabled = (
    subFeatures: ConfigFeatures | undefined,
    packetFeatures: (PacketConfigFeatures | undefined)[],
): boolean => {
    if (!isFeatureEnabled(subFeatures, 'enablePersonalFleet')) return false;
    if (!isFeatureOverridable(subFeatures, 'enablePersonalFleet')) return true;
    const packets = packetFeatures.filter((features) => features != null);
    return packets.length === 0 || packets.some((features) => isPacketFeatureEnabled(features, 'enablePersonalFleet'));
};

/**
 * Whether the group Planning screen is available.
 *
 * Strict AND: planned reservations must be on (`features.enableReservation`), AND the subscription
 * must grant access to the planning ("Local admin can access planning" —
 * `adminOptions.seePlanification`). Like `enableWebAccess`, that flag's spec default is `false` and it
 * is read strictly with NO default fallback: planning stays opt-in per subscription, so an unset value
 * keeps it hidden. Applies to every role — only local admins are ever offered a planning entry today,
 * and the screen guard must agree with the entries that lead to it.
 */
export const isPlanningEnabled = (
    subFeatures: ConfigFeatures | undefined,
    adminOptions: ConfigAdminOptions | undefined,
): boolean =>
    isFeatureEnabled(subFeatures, 'enableReservation') && adminOptions?.seePlanification === true;

/** Whether a packet payment feature is enabled (explicit value, else its spec default, else false). */
export const isPaymentEnabled = (payment: PacketConfigPayment | undefined, key: PaymentKey): boolean =>
    (payment?.[key] ?? paymentDefaults[key] ?? false) === true;

/**
 * Whether the end-user "My access" (reservation profiles / pin codes) feature is shown.
 *
 * Strict AND across THREE layers:
 *   - the subscription allows access profiles          (`features.enableAccessProfile`)
 *   - the group's packet allows them too               (`features.enableAccessProfile`)
 *   - end users may manage them FROM THE WEB APP       (`adminOptions.enableWebAccess`)
 *
 * The first two describe "instant reservations" — access profiles existing at all — and are resolved
 * against their spec defaults like every other gate here, so a subscription or packet that never
 * explicitly stored the flag reads as its declared default (`true`) rather than as hidden. Skipping
 * the defaults would INVERT these two layers, hiding the feature for every record predating the
 * config sub-components.
 *
 * The third layer is what governs this app specifically ("End user can manage access profiles on
 * web"). Its spec default is `false` and it is read strictly, with NO default fallback: the web
 * surface stays opt-in per subscription, so an unset value keeps the feature hidden.
 */
export const isAccessProfileEnabled = (
    subFeatures: ConfigFeatures | undefined,
    packetFeatures: PacketConfigFeatures | undefined,
    adminOptions: ConfigAdminOptions | undefined,
): boolean =>
    isFeatureEnabled(subFeatures, 'enableAccessProfile')
    && isPacketFeatureEnabled(packetFeatures, 'enableAccessProfile')
    && adminOptions?.enableWebAccess === true;
